import React, { useEffect, useState } from "react";
import axios from "axios";
import styled from "styled-components";

import { NewsItem as NewsItemType } from "types";
import NewsItem from "./ChildComponents/NewsItem";
import Tip from "components/Common/misc/Tip";

import { NEWS_FEED as newsFeedTip } from "data/tips";
import { getAutoScroll, setAutoScroll } from "../../../../../../firebase";
import { useAuth } from "contexts/AuthContext";
import { useSelector } from "react-redux";
import { RootState } from "redux/reducers";

// https://www.sitepoint.com/community/t/auto-scrolling-a-div-with-overflow-scroll-auto/2291/3
let scrollRate = 50;
let div, scrollInterval;
let reachedMaxScroll = false;
const divId = "container";

let prevScrollTop = 0;

function scrollDivInit() {
  div = document.getElementById(divId);
  div.scrollTop = 0;

  scrollInterval = setInterval(scrollDiv, scrollRate);
}

function scrollDiv() {
  if (!reachedMaxScroll) {
    div.scrollTop = prevScrollTop;
    prevScrollTop++;

    reachedMaxScroll = div.scrollTop >= div.scrollHeight - div.offsetHeight;
  } else {
    reachedMaxScroll = div.scrollTop === 0 ? false : true;

    div.scrollTop = prevScrollTop;
    prevScrollTop--;
  }
  return;
}

function pauseDiv() {
  clearInterval(scrollInterval);
}

function resumeDiv() {
  div = document.getElementById(divId);
  prevScrollTop = div.scrollTop;
  scrollInterval = setInterval(scrollDiv, scrollRate);
}

const NewsFeed = () => {
  const { report } = useSelector((state: RootState) => state);
  const { currentUser } = useAuth();
  const [data, setData] = useState<NewsItemType[]>([]);
  const [scroll, setScroll] = useState<boolean>(false);

  useEffect(() => {
    axios.get(`/news?sub_category_id=${report.reportMeta.sub_category_id}`)
      .then((response) => {
        const seen = new Set();
        const uniqueNews = response.data.filter(item => {
          const duplicate = seen.has(item.title);
          seen.add(item.title);
          return !duplicate;
        });

        if (uniqueNews.length > 0) {
          setData(uniqueNews);
        }
      });

    scrollDivInit();

    getAutoScroll(currentUser).then((response) => {
      setScroll(response);
    });
  }, []);

  useEffect(() => {
    if (scroll) {
      resumeDiv();
    } else {
      pauseDiv();
    }
  }, [scroll])

  async function handleAutoScroll() {
    setScroll(!scroll);
    setAutoScroll(currentUser, !scroll);
  }

  const listItems = data.map((item) => (
    <NewsItem
      key={item.title}
      title={item.title}
      link={item.link}
      pubDate={item.pubDate}
      contentSnippet={item.contentSnippet}
    />
  ));

  return (
    <Container>
      <Header>
        <div>
          News Feed
          {/* <Tip text={newsFeedTip} bottom={-85} right={-35} width={165} /> */}
        </div>
        <div>
          <label htmlFor="auto-scroll" onClick={handleAutoScroll}>
            Auto-scroll
          </label>
          <Switch type="checkbox" name="auto-scroll" onChange={handleAutoScroll} checked={scroll} />
        </div>
      </Header>

      <List
        id="container"
        onMouseOver={() => {
          if (scroll) pauseDiv();
        }}
        onMouseOut={() => {
          if (scroll) resumeDiv();
        }}
      >
        {listItems}
      </List>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 50%;
  margin: 1rem 0;
  height: 100%;
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  position: sticky;
  top: 0;
  z-index: 10;
  margin-bottom: 1rem;
  
  background-color: ${(props) => props.theme.colors.lighterGrey};
  border: 0;
  /* border-radius: 5rem; */
  color: ${(props) => props.theme.colors.idataDarkGrey};
  font-size: 2.5rem;
  padding: 3rem 1.25rem;
  height: 5rem;

  label {
    font-size: 2rem;
  }

  > div {
    display: flex;
    gap: 12px;
    align-items: center;
    justify-content: center;
    height: 6rem;;
  }

  > div:first-of-type {
    font-weight: 500;
  }
`;

const List = styled.div`
  overflow-y: scroll;
`;

const Switch = styled.input`
  /*! uiswitch v1.1.0 | MIT License | github.com/fnky/css3-uiswitch */

  height: 31px;
  width: 51px;
  -webkit-appearance: none;
     -moz-appearance: none;
          appearance: none;
  box-sizing: border-box;
  position: relative;
  border-radius: 16px;
  cursor: pointer;
  outline: 0;
  z-index: 0;
  margin: 0;
  padding: 0;
  border: none;
  background-color: #e5e5e5;
  -webkit-touch-callout: none;
  -webkit-text-size-adjust: none;
  -webkit-tap-highlight-color: transparent;
  -webkit-user-select: none;

  &::before {
    height: 27px;
    width: 47px;
    box-sizing: border-box;
    content: ' ';
    position: absolute;
    left: 2px;
    top: 2px;
    background-color: #ffffff;
    border-radius: 16px;
    z-index: 1;
    -webkit-transition-duration: 300ms;
            transition-duration: 300ms;
    -webkit-transform: scale(1);
            transform: scale(1);
  }

  &::after {
    height: 27px;
    width: 27px;
    box-sizing: border-box;
    content: ' ';
    position: absolute;
    border-radius: 27px;
    background: #ffffff;
    z-index: 2;
    top: 2px;
    left: 2px;
    box-shadow: 0px 0px 1px 0px rgba(0, 0, 0, 0.25), 0px 4px 11px 0px rgba(0, 0, 0, 0.08), -1px 3px 3px 0px rgba(0, 0, 0, 0.14);
    -webkit-transition: width 280ms, -webkit-transform 300ms;
    transition: width 280ms, -webkit-transform 300ms;
    transition: transform 300ms, width 280ms;
    transition: transform 300ms, width 280ms, -webkit-transform 300ms;
    -webkit-transform: translate3d(0, 0, 0);
            transform: translate3d(0, 0, 0);
    -webkit-transition-timing-function: cubic-bezier(0.42, 0.8, 0.58, 1.2);
            transition-timing-function: cubic-bezier(0.42, 0.8, 0.58, 1.2);
  }

  &:checked {
    background-image: -webkit-linear-gradient(top, #4CD964 0%, #4dd865 100%);
    background-image: linear-gradient(-180deg, #4CD964 0%, #4dd865 100%);
  }

  &:checked::after {
    -webkit-transform: translate3d(16px, 0, 0);
            transform: translate3d(16px, 0, 0);
    right: 18px;
    left: inherit;
  }

  &:active::after {
    width: 35px;
  }

  &:checked::before, &:active::before {
    -webkit-transform: scale(0);
            transform: scale(0);
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
    -webkit-transition: none;
    transition: none;
  }

  &:disabled:active::before, &:disabled:active::after, &:disabled:checked:active::before, &:disabled:checked::before {
    width: 27px;
    -webkit-transition: none;
    transition: none;
  }

  &:disabled:active::before {
    height: 27px;
    width: 47px;
    -webkit-transform: translate3d(6px, 0, 0);
            transform: translate3d(6px, 0, 0);
  }

  &:disabled:checked:active::before {
    height: 27px;
    width: 27px;
    -webkit-transform: scale(0);
            transform: scale(0);
  }
  
  background-color: #e5e5e5;

  &::before {
    background-color: #ffffff;
  }

  &::after {
    background: #ffffff;
  }

  &:checked {
    background-image: -webkit-linear-gradient(top, #4CD964 0%, #4dd865 100%);
    background-image: linear-gradient(-180deg, #4CD964 0%, #4dd865 100%);
  }

  /* Custom (Huy Aug 2024) */
  transition: all 0.25s;

  &:checked {
    background-image: none !important;
    background-color: ${(props) => props.theme.colors.idataBlue};
  }

  &:hover {
    opacity: 0.8;
  }
`;

export default NewsFeed;
